import React, { useState, useEffect } from 'react';
import './AdultsPreview.css';

const COURSE_1_TOPICS = [
  'Email & Communication',
  'Internet Safety',
  'AI in Simple Terms',
  'Smartphones & Apps',
  'Digital Photos & Video',
];

const COURSE_2_TOPICS = [
  'Digital Banking',
  'Budgeting Tools & Apps',
  'Fraud Prevention',
  'Online Payments & Safety',
  'Memory & Focus Activities',
];

const LOCATIONS = [
  { city: 'Milpitas',    emoji: '📍', state: 'Santa Clara County, CA' },
  { city: 'Pleasanton',  emoji: '📍', state: 'Alameda County, CA' },
  { city: 'San Mateo',   emoji: '📍', state: 'San Mateo County, CA' },
];

/* ── QUIZ DATA ──────────────────────────────────────── */
const COURSE_1_QUIZZES = {
  'Email & Communication': [
    {
      question: 'What is an email address used for?',
      options: [
        'Storing your photos online',
        'Sending and receiving electronic messages',
        'Browsing the internet',
        'Making phone calls',
      ],
      answer: 1,
    },
    {
      question: 'What does "CC" mean in an email?',
      options: [
        'Cancel Communication',
        'Carbon Copy — sends a copy to additional recipients',
        'Confidential Content',
        'Computer Code',
      ],
      answer: 1,
    },
    {
      question: 'What should you do before clicking a link in an email from an unknown sender?',
      options: [
        'Click it immediately to see what it is',
        'Forward it to all your contacts',
        'Hover over it to check the web address, or delete the email',
        'Reply asking for more information',
      ],
      answer: 2,
    },
    {
      question: 'What is an email attachment?',
      options: [
        'A sticky note you add to your monitor',
        'A file (document, photo, etc.) sent along with an email',
        'The subject line of an email',
        'The email address of the sender',
      ],
      answer: 1,
    },
    {
      question: 'What is the purpose of the subject line in an email?',
      options: [
        'It replaces the body of the email',
        'It contains your password',
        'It briefly describes the topic of the email',
        'It lists all the recipients',
      ],
      answer: 2,
    },
    {
      question: 'What is spam email?',
      options: [
        'An email with a large attachment',
        'An important email from your doctor',
        'Unwanted or junk email, often advertising or scam messages',
        'An email you have already read',
      ],
      answer: 2,
    },
    {
      question: 'What does "Reply All" do when responding to an email?',
      options: [
        'Sends your reply only to the original sender',
        'Deletes all emails in your inbox',
        'Sends your reply to everyone included in the original email',
        'Forwards the email to a new contact',
      ],
      answer: 2,
    },
    {
      question: 'Which of these is a warning sign that an email might be a scam?',
      options: [
        'It comes from a friend you recognize',
        'It has a clear subject line and proper spelling',
        'It urgently asks for your password, money, or personal information',
        'It includes your name in the greeting',
      ],
      answer: 2,
    },
    {
      question: 'What does it mean to "forward" an email?',
      options: [
        'Move the email to a folder',
        'Send a received email on to another person',
        'Delete the email permanently',
        'Print the email on paper',
      ],
      answer: 1,
    },
    {
      question: 'What is a good habit when managing your email inbox?',
      options: [
        'Never delete any emails',
        'Share your email password with family members',
        'Regularly delete or organize emails and unsubscribe from unwanted lists',
        'Keep all emails in your inbox without folders',
      ],
      answer: 2,
    },
  ],

  'Internet Safety': [
    {
      question: 'Which of the following is the strongest password?',
      options: [
        'password123',
        'Your pet\'s name',
        'A mix of letters, numbers, and symbols (e.g., R7!mLx@9)',
        'Your birthday',
      ],
      answer: 2,
    },
    {
      question: 'What does HTTPS at the start of a website address mean?',
      options: [
        'The website is from a government agency',
        'The connection to the website is encrypted and more secure',
        'The website is free to use',
        'The website has no advertisements',
      ],
      answer: 1,
    },
    {
      question: 'What is "phishing"?',
      options: [
        'A sport played near the ocean',
        'A trick to get your personal information by pretending to be a trusted source',
        'A way to speed up your internet connection',
        'A method of organizing your files',
      ],
      answer: 1,
    },
    {
      question: 'A pop-up on your screen says "You won $1,000! Click here!" What should you do?',
      options: [
        'Click it immediately to claim your prize',
        'Share it with friends so they can win too',
        'Close it — it is almost certainly a scam',
        'Enter your credit card details to receive the prize',
      ],
      answer: 2,
    },
    {
      question: 'What is two-factor authentication (2FA)?',
      options: [
        'Using two different computers to log in',
        'A second step (like a text code) that confirms it is really you logging in',
        'Having two separate email accounts',
        'Typing your password twice',
      ],
      answer: 1,
    },
    {
      question: 'What is malware?',
      options: [
        'A type of helpful software update',
        'Bad software designed to damage your device or steal information',
        'A brand of laptop computer',
        'A slow internet connection',
      ],
      answer: 1,
    },
    {
      question: 'What is the safest thing to do on public Wi-Fi (like in a coffee shop)?',
      options: [
        'Do all your online banking immediately',
        'Share your home address with a website',
        'Avoid logging in to banking or sensitive accounts; use a VPN if possible',
        'Download as many apps as possible',
      ],
      answer: 2,
    },
    {
      question: 'How often should you update your important passwords?',
      options: [
        'Never — changing passwords is confusing',
        'Once every 10 years',
        'Regularly (every few months) and immediately after any security breach',
        'Only when you forget them',
      ],
      answer: 2,
    },
    {
      question: 'What does antivirus software do?',
      options: [
        'Makes your computer run faster',
        'Helps prevent colds and flu',
        'Detects and removes harmful programs from your computer',
        'Saves your photos to the cloud',
      ],
      answer: 2,
    },
    {
      question: 'What information should you NEVER share online with strangers?',
      options: [
        'Your favorite color',
        'A book recommendation',
        'Your Social Security number, bank details, or home address',
        'The name of a movie you enjoyed',
      ],
      answer: 2,
    },
  ],

  'AI in Simple Terms': [
    {
      question: 'What does "AI" stand for?',
      options: [
        'Automated Internet',
        'Artificial Intelligence',
        'Advanced Imaging',
        'Automatic Input',
      ],
      answer: 1,
    },
    {
      question: 'How does AI "learn" to do things?',
      options: [
        'It reads textbooks like a student',
        'A programmer teaches it every single answer',
        'It studies patterns in large amounts of data',
        'It copies what humans do exactly, step by step',
      ],
      answer: 2,
    },
    {
      question: 'Which of these is an everyday example of AI?',
      options: [
        'A light switch turning on',
        'A voice assistant (like Siri or Alexa) answering your question',
        'A microwave heating food',
        'A ceiling fan spinning',
      ],
      answer: 1,
    },
    {
      question: 'What is a chatbot?',
      options: [
        'A small robot that cleans your house',
        'A computer program that can have a text conversation with you',
        'A type of smartphone app for video calls',
        'A machine that prints documents',
      ],
      answer: 1,
    },
    {
      question: 'What does a "recommendation system" do?',
      options: [
        'Recommends doctors to visit',
        'Suggests products, shows, or articles based on your interests',
        'Fixes problems on your computer automatically',
        'Sorts your email into folders',
      ],
      answer: 1,
    },
    {
      question: 'What is voice recognition technology?',
      options: [
        'A speaker that plays music loudly',
        'Technology that can understand and respond to spoken words',
        'A microphone used for recording music',
        'A hearing aid for seniors',
      ],
      answer: 1,
    },
    {
      question: 'AI on a smartphone might help you by:',
      options: [
        'Replacing the battery when it runs out',
        'Automatically suggesting words as you type a text message',
        'Printing documents wirelessly',
        'Charging your phone faster',
      ],
      answer: 1,
    },
    {
      question: 'Is all AI perfect and always correct?',
      options: [
        'Yes — AI never makes mistakes',
        'No — AI can make errors and should not replace expert advice (like from doctors)',
        'Yes — if it comes from a computer it must be right',
        'No — AI is broken and should not be used at all',
      ],
      answer: 1,
    },
    {
      question: 'What is one benefit of AI for everyday tasks?',
      options: [
        'It can help with tasks like translating languages, reading maps, or identifying plants in photos',
        'It replaces the need for all doctors',
        'It makes everything completely free',
        'It eliminates the need for electricity',
      ],
      answer: 0,
    },
    {
      question: 'What should you be careful about when using AI-generated information?',
      options: [
        'Nothing — AI is always trustworthy',
        'The color of the text on the screen',
        'Always verify important information with reliable sources, since AI can be wrong',
        'The speed of your internet connection',
      ],
      answer: 2,
    },
  ],

  'Smartphones & Apps': [
    {
      question: 'What is an "app" on a smartphone?',
      options: [
        'A physical button on the side of the phone',
        'A small software program you can download and use on your phone',
        'The phone\'s battery charger',
        'The phone\'s camera lens',
      ],
      answer: 1,
    },
    {
      question: 'Where do you go to install new apps on most smartphones?',
      options: [
        'The Settings menu',
        'The App Store (iPhone) or Google Play Store (Android)',
        'The phone\'s alarm clock',
        'The calculator app',
      ],
      answer: 1,
    },
    {
      question: 'Why is it important to keep your phone\'s software updated?',
      options: [
        'Updates make your phone use more battery',
        'Updates fix security issues and improve how the phone works',
        'Updates delete all your photos',
        'Updates are optional and have no real benefit',
      ],
      answer: 1,
    },
    {
      question: 'What is "mobile data"?',
      options: [
        'Photos stored in your phone',
        'The internet connection your phone uses when not on Wi-Fi',
        'Your phone\'s contact list',
        'The apps downloaded on your phone',
      ],
      answer: 1,
    },
    {
      question: 'What does "airplane mode" do on a smartphone?',
      options: [
        'Turns on a flight simulator game',
        'Brightens the screen for outdoor use',
        'Turns off all wireless connections (calls, Wi-Fi, data)',
        'Saves all your photos automatically',
      ],
      answer: 2,
    },
    {
      question: 'What is a safe practice when downloading apps?',
      options: [
        'Download apps from any website you find online',
        'Only download from official app stores and check reviews first',
        'Download as many free apps as possible',
        'Ignore all permission requests the app makes',
      ],
      answer: 1,
    },
    {
      question: 'What is a QR code?',
      options: [
        'A type of secret password',
        'A square barcode you scan with your camera to open a website or information',
        'A signal that your Wi-Fi is working',
        'A backup for your contacts',
      ],
      answer: 1,
    },
    {
      question: 'What can you do to preserve your phone\'s battery life?',
      options: [
        'Keep the screen brightness at maximum at all times',
        'Leave all apps running in the background',
        'Lower screen brightness, turn off Wi-Fi/Bluetooth when not in use',
        'Never charge the phone fully',
      ],
      answer: 2,
    },
    {
      question: 'What are accessibility settings on a smartphone for?',
      options: [
        'They only control the ringtone',
        'They make the phone harder to use',
        'They help people with visual, hearing, or motor needs (e.g., larger text, voice control)',
        'They delete unused apps',
      ],
      answer: 2,
    },
    {
      question: 'You receive a text from an unknown number with a suspicious link. What should you do?',
      options: [
        'Click the link to see what it leads to',
        'Reply with your name and address',
        'Do not click the link; delete the message or report it as spam',
        'Forward it to all your contacts',
      ],
      answer: 2,
    },
  ],

  'Digital Photos & Video': [
    {
      question: 'What is a digital photo?',
      options: [
        'A photo printed at a pharmacy',
        'An image captured and stored as data on a device like a phone or camera',
        'A hand-drawn picture scanned into a computer',
        'A photo album kept in a drawer',
      ],
      answer: 1,
    },
    {
      question: 'What does "resolution" mean in a photo?',
      options: [
        'The color of the photo\'s background',
        'The size and detail quality of the image — higher resolution means sharper photos',
        'The speed at which the photo is taken',
        'The name you give to a photo file',
      ],
      answer: 1,
    },
    {
      question: 'What is cloud storage for photos?',
      options: [
        'Printing photos and storing them in a cloud-shaped box',
        'Saving photos to the internet so they are safe even if your phone is lost',
        'A weather app that takes photos of clouds',
        'A special type of USB drive shaped like a cloud',
      ],
      answer: 1,
    },
    {
      question: 'What is a video call?',
      options: [
        'Watching a movie on your phone',
        'Sending a video file to someone',
        'A live call where you and the other person can see each other on screen',
        'Recording a video message and mailing it',
      ],
      answer: 2,
    },
    {
      question: 'What is a screenshot?',
      options: [
        'A photo taken with the front camera',
        'A recording of your entire screen saved as an image',
        'A blurry photo caused by camera shake',
        'A photo taken outdoors',
      ],
      answer: 1,
    },
    {
      question: 'What should you consider before posting a photo of another person online?',
      options: [
        'Nothing — you can post any photo you want',
        'Whether you have their permission, since posting without consent can violate their privacy',
        'Only the lighting quality of the photo',
        'Whether the photo has a filter applied',
      ],
      answer: 1,
    },
    {
      question: 'How can you organize your digital photos?',
      options: [
        'Leave all photos in a single unorganized folder',
        'Delete photos regularly since storage is unlimited',
        'Create folders by date or event, and use apps like Google Photos for automatic organization',
        'Print every photo and store them in boxes',
      ],
      answer: 2,
    },
    {
      question: 'What is a photo "filter" when used on a smartphone?',
      options: [
        'A physical lens attachment for a camera',
        'A digital effect that changes the look, color, or mood of a photo',
        'A setting that removes people from photos',
        'A way to reduce the file size of a photo',
      ],
      answer: 1,
    },
    {
      question: 'Which app is commonly used for video calls with family and friends?',
      options: [
        'A calendar app',
        'A weather app',
        'FaceTime, Zoom, or WhatsApp video',
        'A calculator app',
      ],
      answer: 2,
    },
    {
      question: 'You want to back up your photos so they are safe. What is the best approach?',
      options: [
        'Keep them only on your phone and hope it never breaks',
        'Back them up to a cloud service (like Google Photos or iCloud) and/or an external drive',
        'Email every photo to yourself one at a time',
        'Photos cannot be backed up',
      ],
      answer: 1,
    },
  ],
};

const COURSE_2_QUIZZES = {
  'Digital Banking': [
    {
      question: 'What is online banking?',
      options: [
        'Visiting your bank branch in person every day',
        'Managing your bank account (checking balances, transfers, bills) through the internet',
        'A type of savings account with high interest',
        'Withdrawing cash from an ATM',
      ],
      answer: 1,
    },
    {
      question: 'What should you always check for before logging in to your bank online?',
      options: [
        'Whether the page has colorful graphics',
        'The HTTPS padlock icon and correct website address (URL)',
        'How many advertisements are on the page',
        'Whether the page loads quickly',
      ],
      answer: 1,
    },
    {
      question: 'What is a mobile banking app?',
      options: [
        'A cash register at a store',
        'An app on your phone that lets you manage your bank account anywhere',
        'A credit card you use at the grocery store',
        'A machine that counts coins',
      ],
      answer: 1,
    },
    {
      question: 'What does it mean to "transfer funds" in online banking?',
      options: [
        'Move money from one account to another',
        'Print your bank statement',
        'Open a new savings account',
        'Request a new debit card',
      ],
      answer: 0,
    },
    {
      question: 'What is a bank statement?',
      options: [
        'A letter asking you to open an account',
        'A record of all your account transactions (deposits, withdrawals) over a period of time',
        'A form to apply for a loan',
        'A government tax document',
      ],
      answer: 1,
    },
    {
      question: 'Why should you set up account alerts on your bank account?',
      options: [
        'To receive advertising from the bank',
        'They have no real benefit',
        'To get notified of transactions so you can spot unauthorized activity quickly',
        'To automatically invest your money',
      ],
      answer: 2,
    },
    {
      question: 'What is a debit card?',
      options: [
        'A card that lets you borrow money from the bank',
        'A card linked directly to your bank account that uses money you already have',
        'A gift card you buy at a store',
        'An ID card issued by your bank',
      ],
      answer: 1,
    },
    {
      question: 'What should you do if you notice a transaction on your bank account that you do not recognize?',
      options: [
        'Ignore it and assume it is correct',
        'Wait several months to see if it disappears',
        'Contact your bank immediately to report it and dispute the charge',
        'Close your bank account permanently',
      ],
      answer: 2,
    },
    {
      question: 'Is it safe to log in to your bank on a shared public computer (e.g., at a library)?',
      options: [
        'Yes — all public computers are secure',
        'No — avoid it if possible; if you must, always log out completely and clear the browser',
        'Yes — banks always protect your information automatically',
        'No — you should never use online banking at all',
      ],
      answer: 1,
    },
    {
      question: 'What is the purpose of a PIN (Personal Identification Number)?',
      options: [
        'It is your account number printed on your card',
        'It is a secret code that verifies your identity when using a debit card or ATM',
        'It is a code the bank sends you in the mail',
        'It is the routing number for wire transfers',
      ],
      answer: 1,
    },
  ],

  'Budgeting Tools & Apps': [
    {
      question: 'What is the main purpose of a budget?',
      options: [
        'To spend as much money as possible',
        'To plan how you will use your income so you can cover expenses and save',
        'To track how many apps you have on your phone',
        'To invest in the stock market',
      ],
      answer: 1,
    },
    {
      question: 'What is a budgeting app?',
      options: [
        'An app that automatically spends your money for you',
        'A software tool that helps you track income, expenses, and savings goals',
        'A game involving virtual money',
        'An app that applies for loans',
      ],
      answer: 1,
    },
    {
      question: 'What does "fixed expense" mean?',
      options: [
        'An expense that varies each month like groceries',
        'A one-time luxury purchase',
        'An expense that stays the same each month, like rent or a car payment',
        'Money you save in a piggy bank',
      ],
      answer: 2,
    },
    {
      question: 'What is a "variable expense"?',
      options: [
        'A bill that is the same amount every month',
        'A cost that changes from month to month, such as dining out or entertainment',
        'The total of all your monthly bills',
        'A type of investment account',
      ],
      answer: 1,
    },
    {
      question: 'What does it mean to "track your spending"?',
      options: [
        'Memorize every price at the grocery store',
        'Record all the money you spend so you can see where it goes',
        'Ask your bank to limit your purchases',
        'Only use cash instead of cards',
      ],
      answer: 1,
    },
    {
      question: 'Which of these is a popular free budgeting app or tool?',
      options: [
        'Microsoft Word',
        'Mint or a spreadsheet like Google Sheets',
        'A weather forecasting app',
        'A video streaming service',
      ],
      answer: 1,
    },
    {
      question: 'What is an "emergency fund"?',
      options: [
        'Money set aside specifically for unexpected expenses (medical bills, car repair, etc.)',
        'A fund for buying luxury items',
        'The balance in your checking account',
        'A government benefit program',
      ],
      answer: 0,
    },
    {
      question: 'If your monthly expenses are higher than your income, what should you do?',
      options: [
        'Ignore the difference and continue spending',
        'Take out a loan immediately',
        'Look for expenses you can reduce or find ways to increase your income',
        'Close all your bank accounts',
      ],
      answer: 2,
    },
    {
      question: 'What is the benefit of setting up automatic savings transfers?',
      options: [
        'Your bank keeps the money permanently',
        'It ensures you save consistently without relying on willpower each month',
        'It earns double the interest',
        'It prevents you from spending money at stores',
      ],
      answer: 1,
    },
    {
      question: 'What does "net income" mean?',
      options: [
        'The total amount you earn before any deductions',
        'The amount you earn after taxes and deductions are taken out — your take-home pay',
        'Money borrowed from a bank',
        'The total value of everything you own',
      ],
      answer: 1,
    },
  ],

  'Fraud Prevention': [
    {
      question: 'What is identity theft?',
      options: [
        'Losing your wallet',
        'When someone uses your personal information (name, SSN, etc.) without permission for financial gain',
        'Forgetting your PIN number',
        'Receiving too many emails',
      ],
      answer: 1,
    },
    {
      question: 'You receive a call from someone claiming to be from the IRS saying you owe taxes and must pay immediately with gift cards. What should you do?',
      options: [
        'Buy the gift cards and give them the numbers right away',
        'Give them your Social Security number to verify your identity',
        'Hang up — this is a well-known government impersonation scam',
        'Call back the number they provide',
      ],
      answer: 2,
    },
    {
      question: 'What is a "grandparent scam"?',
      options: [
        'A discount offered to seniors at stores',
        'A fraud where scammers pretend to be a grandchild in trouble and ask for money',
        'A program that helps grandparents use technology',
        'A legal way to transfer money to family',
      ],
      answer: 1,
    },
    {
      question: 'Which of these is a sign that a phone call may be fraudulent?',
      options: [
        'The caller identifies themselves and offers a callback number you can verify',
        'The caller creates urgency and demands payment by gift card, wire transfer, or cryptocurrency',
        'The caller asks you to call back your bank\'s official number',
        'The caller leaves a voicemail with their name',
      ],
      answer: 1,
    },
    {
      question: 'What should you do to protect yourself from fraud?',
      options: [
        'Share your personal information freely so companies can help you',
        'Never shred documents — recycling is better',
        'Monitor your financial accounts regularly and shred documents with personal info',
        'Use the same password for all accounts to remember it easily',
      ],
      answer: 2,
    },
    {
      question: 'What is a "lottery scam"?',
      options: [
        'A real lottery game run by the state',
        'A fraud where you are told you won a prize but must pay fees or give personal info first',
        'A raffle at a local charity event',
        'A discount coupon for a sweepstakes',
      ],
      answer: 1,
    },
    {
      question: 'What should you do if you think you have been a victim of fraud?',
      options: [
        'Do nothing and hope the problem resolves itself',
        'Report it to your bank, local police, and the FTC (Federal Trade Commission) immediately',
        'Tell only your neighbors',
        'Wait several months before taking action',
      ],
      answer: 1,
    },
    {
      question: 'A stranger at the store asks for your Medicare or Social Security number to "verify your benefits." What should you do?',
      options: [
        'Provide the numbers — they may be a healthcare worker',
        'Refuse — legitimate organizations will not approach you randomly for this information',
        'Write it down on paper for them',
        'Call your family member and give the information over the phone',
      ],
      answer: 1,
    },
    {
      question: 'What is a credit freeze, and why is it useful?',
      options: [
        'A way to prevent your credit card from working in cold weather',
        'A free action that prevents new credit accounts from being opened in your name, protecting against identity theft',
        'A penalty your bank applies for overdrafts',
        'A lock placed on your checking account',
      ],
      answer: 1,
    },
    {
      question: 'Which of these is a legitimate way a bank or government agency will contact you?',
      options: [
        'Demanding immediate payment via gift cards over the phone',
        'Asking for your full password to "verify your account"',
        'Sending an official letter or directing you to log in to your own account through the official website',
        'Threatening arrest unless you pay right away',
      ],
      answer: 2,
    },
  ],

  'Online Payments & Safety': [
    {
      question: 'What is PayPal?',
      options: [
        'A type of credit card',
        'An online payment service that lets you send and receive money electronically',
        'A government payment system',
        'A mobile phone provider',
      ],
      answer: 1,
    },
    {
      question: 'When shopping online, how do you know a website\'s checkout is secure?',
      options: [
        'The website has many colorful pictures',
        'The URL begins with "https://" and shows a padlock icon',
        'The prices are lower than in stores',
        'The website has a lot of customer reviews',
      ],
      answer: 1,
    },
    {
      question: 'What is a credit card "chargeback"?',
      options: [
        'A fee for using your credit card abroad',
        'When the bank reverses a charge on your card, often when you dispute a fraudulent transaction',
        'A reward point earned from purchases',
        'A penalty for paying your bill late',
      ],
      answer: 1,
    },
    {
      question: 'Why is it safer to use a credit card rather than a debit card for online shopping?',
      options: [
        'Credit cards have higher spending limits',
        'Credit cards offer stronger fraud protection and do not directly expose your bank account funds',
        'Debit cards cannot be used online',
        'Credit cards are free to use',
      ],
      answer: 1,
    },
    {
      question: 'What is a digital wallet (like Apple Pay or Google Pay)?',
      options: [
        'A leather wallet with a computer chip',
        'An app that stores your payment information securely for easy contactless payments',
        'An online savings account',
        'A subscription service for financial advice',
      ],
      answer: 1,
    },
    {
      question: 'Before entering your payment information on a website, what should you verify?',
      options: [
        'That the website has a pretty design',
        'That the website is legitimate, uses HTTPS, and you can find reviews confirming it is trustworthy',
        'That the website has been around for less than a year',
        'That the website offers free shipping',
      ],
      answer: 1,
    },
    {
      question: 'A website asks for your Social Security number to complete a routine online purchase. What should you do?',
      options: [
        'Provide it — all websites need this',
        'Refuse — a legitimate shopping site should never need your SSN',
        'Provide your date of birth instead',
        'Send it by email to be safe',
      ],
      answer: 1,
    },
    {
      question: 'What is a virtual (one-time use) credit card number?',
      options: [
        'A credit card mailed to your home',
        'A temporary card number generated for a single transaction to protect your real card details',
        'A credit card issued by an online-only bank',
        'A prepaid gift card from a store',
      ],
      answer: 1,
    },
    {
      question: 'You receive an email receipt for a purchase you did not make. What should you do?',
      options: [
        'Ignore it — it is probably spam',
        'Click the link in the email to cancel the order',
        'Contact your bank or the merchant directly using their official website — not links in the email',
        'Reply to the email with your account details',
      ],
      answer: 2,
    },
    {
      question: 'What does it mean to "check out as a guest" on a shopping website?',
      options: [
        'You get a discount for being a new visitor',
        'You complete a purchase without creating an account, which means less personal data is stored',
        'You can only browse but not buy anything',
        'You must have a coupon code to proceed',
      ],
      answer: 1,
    },
  ],

  'Memory & Focus Activities': [
    {
      question: 'Which of these habits is known to support brain health as we age?',
      options: [
        'Watching many hours of TV without breaks',
        'Regular physical exercise, quality sleep, and mental challenges',
        'Eating as much sugar as possible',
        'Avoiding all social interaction',
      ],
      answer: 1,
    },
    {
      question: 'What is a benefit of learning new skills (like using a computer) for brain health?',
      options: [
        'It has no effect on the brain',
        'It creates new neural connections and can help keep the mind sharp',
        'It is only beneficial for children under 18',
        'It causes stress that is bad for memory',
      ],
      answer: 1,
    },
    {
      question: 'What does "neuroplasticity" mean in simple terms?',
      options: [
        'Plastic surgery for the nervous system',
        'The brain\'s ability to change and form new connections throughout life',
        'A type of memory loss condition',
        'The physical weight of the brain',
      ],
      answer: 1,
    },
    {
      question: 'Which of these activities is a good mental exercise for focus and memory?',
      options: [
        'Staring at a blank wall for an hour',
        'Puzzles, crosswords, learning a new language, or playing a musical instrument',
        'Skipping meals to save time for other activities',
        'Avoiding reading to rest your eyes',
      ],
      answer: 1,
    },
    {
      question: 'How does quality sleep affect memory?',
      options: [
        'Sleep has no effect on memory',
        'During sleep, the brain consolidates memories and clears waste products — making sleep vital for recall',
        'Less sleep improves focus and creativity',
        'Sleep only matters for physical recovery, not brain function',
      ],
      answer: 1,
    },
    {
      question: 'What is mindfulness, and how can it help with focus?',
      options: [
        'A type of aerobic exercise',
        'Paying attention to the present moment with calm awareness — it reduces stress and improves concentration',
        'A medication for memory loss',
        'A way of memorizing long lists of numbers',
      ],
      answer: 1,
    },
    {
      question: 'Which of these foods is linked to better brain health?',
      options: [
        'Highly processed snack foods high in sugar and sodium',
        'Foods rich in omega-3 fatty acids (like salmon, walnuts) and antioxidants (like blueberries)',
        'Energy drinks with high caffeine',
        'White bread and sugary cereals',
      ],
      answer: 1,
    },
    {
      question: 'Why is social connection important for cognitive wellness?',
      options: [
        'Social interaction has no effect on the brain',
        'Staying socially engaged stimulates the brain and is linked to lower rates of cognitive decline',
        'Too much social interaction tires the brain permanently',
        'Social connection only matters for emotional wellbeing, not memory',
      ],
      answer: 1,
    },
    {
      question: 'What is a simple technique to help remember things better?',
      options: [
        'Try to memorize everything perfectly on the first try with no repetition',
        'Writing things down, using reminders, and repeating information aloud or in your own words',
        'Avoid reviewing information to prevent boredom',
        'Watch more television for passive learning',
      ],
      answer: 1,
    },
    {
      question: 'Which of these is a warning sign that you should speak with your doctor about your memory?',
      options: [
        'Occasionally forgetting where you put your keys',
        'Forgetting the name of a movie you saw last year',
        'Frequently forgetting important events, getting confused about time/place, or repeating the same questions often',
        'Taking a few extra seconds to recall a phone number',
      ],
      answer: 2,
    },
  ],
};

/* ── COURSE IMAGES ──────────────────────────────────── */
function Course1Image() {
  return (
    <div className="adults-modal-course-image adults-modal-course-image--tech">
      <div className="adults-modal-course-image-grid">
        <div className="adults-modal-course-img-tile">
          <span>💻</span><span className="adults-modal-course-img-label">Computer Skills</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>📧</span><span className="adults-modal-course-img-label">Email</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>🔒</span><span className="adults-modal-course-img-label">Internet Safety</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>🤖</span><span className="adults-modal-course-img-label">AI Basics</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>📱</span><span className="adults-modal-course-img-label">Smartphones</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>📷</span><span className="adults-modal-course-img-label">Photos & Video</span>
        </div>
      </div>
      <p className="adults-modal-course-image-caption">Computer Basics &amp; Modern Technology</p>
    </div>
  );
}

function Course2Image() {
  return (
    <div className="adults-modal-course-image adults-modal-course-image--finance">
      <div className="adults-modal-course-image-grid">
        <div className="adults-modal-course-img-tile">
          <span>🏦</span><span className="adults-modal-course-img-label">Digital Banking</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>📊</span><span className="adults-modal-course-img-label">Budgeting</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>🛡️</span><span className="adults-modal-course-img-label">Fraud Prevention</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>💳</span><span className="adults-modal-course-img-label">Online Payments</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>🧠</span><span className="adults-modal-course-img-label">Memory</span>
        </div>
        <div className="adults-modal-course-img-tile">
          <span>🎯</span><span className="adults-modal-course-img-label">Focus</span>
        </div>
      </div>
      <p className="adults-modal-course-image-caption">FinTech Literacy &amp; Cognitive Wellness</p>
    </div>
  );
}

/* ── COMPONENT ──────────────────────────────────────── */
function AdultsPreview({ onBack, onSelectK12, initialPreview }) {
  const [coursePreview, setCoursePreview] = useState(null);
  // moduleQuiz: { topic, qIdx, selected, answers, done }
  const [moduleQuiz, setModuleQuiz] = useState(null);

  const quizData = coursePreview === 'course1' ? COURSE_1_QUIZZES : COURSE_2_QUIZZES;
  const topics   = coursePreview === 'course1' ? COURSE_1_TOPICS : COURSE_2_TOPICS;

  const openModal = (courseId) => {
    setCoursePreview(courseId);
    setModuleQuiz(null);
    history.replaceState(null, '', `#adults/${courseId}`);
  };
  const closeModal = () => {
    setCoursePreview(null);
    setModuleQuiz(null);
    history.replaceState(null, '', '#adults');
  };

  // Open modal immediately when arriving via a deep link (e.g. #adults/course1)
  useEffect(() => {
    if (initialPreview === 'course1' || initialPreview === 'course2') {
      openModal(initialPreview);
    }
  }, []);

  const startQuiz = (topic) =>
    setModuleQuiz({ topic, qIdx: 0, selected: null, answers: [], done: false });

  const scrollToCurriculum = () => {
    const el = document.getElementById('curriculum');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="adults-root">
      {/* Teal accent bar at top */}
      <div className="adults-topbar" />

      {/* ── NAV ── */}
      <nav className="adults-nav">
        <button className="adults-back-btn" onClick={onBack}>
          ← All Programs
        </button>
        <span className="adults-nav-brand">SiliconValley4U · Adult Programs</span>
        <a
          href="https://www.siliconvalley4u.com/lifelong-learning"
          target="_blank"
          rel="noopener noreferrer"
          className="adults-nav-cta"
        >
          Enroll in a Course →
        </a>
      </nav>

      {/* ── HERO ── */}
      <section className="adults-hero">
        <div className="adults-hero-inner">
          <div className="adults-hero-text">
            <h1>
              Digital Skills for<br />
              <span>Lifelong Learners</span>
            </h1>
            <p className="adults-hero-sub">
              Practical, friendly programs that build confidence with technology,
              finance, and wellness — in a welcoming, supportive environment.
            </p>

            <div className="adults-hero-badges">
              <span className="adults-badge adults-badge-teal">✓ Open to Adult Learners</span>
              <span className="adults-badge adults-badge-sky">⭐ Designed for Adult Learners</span>
              <span className="adults-badge adults-badge-gray">📍 3 Bay Area Locations</span>
            </div>

            <div className="adults-hero-ctas">
              <button className="adults-cta-explore" onClick={scrollToCurriculum}>
                👀 Preview Courses
              </button>
              <a
                href="https://www.siliconvalley4u.com/lifelong-learning"
                target="_blank"
                rel="noopener noreferrer"
                className="adults-cta-primary"
              >
                Enroll Now →
              </a>
              <button className="adults-cta-ghost" onClick={onBack}>
                View All Programs
              </button>
            </div>
          </div>

          {/* Visual stats column */}
          <div className="adults-hero-visual">
            <div className="adults-stat-card">
              <span className="adults-stat-icon">📚</span>
              <div>
                <span className="adults-stat-val">2</span>
                <span className="adults-stat-lbl">Courses Available</span>
              </div>
            </div>
            <div className="adults-stat-card">
              <span className="adults-stat-icon">📍</span>
              <div>
                <span className="adults-stat-val">3</span>
                <span className="adults-stat-lbl">Bay Area Locations</span>
              </div>
            </div>
            <div className="adults-stat-card">
              <span className="adults-stat-icon">👥</span>
              <div>
                <span className="adults-stat-lbl">Popular with 50+ community</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LOCATIONS ── */}
      <div className="adults-section">
        <p className="adults-section-label">Where We Teach</p>
        <h2 className="adults-section-title">3 Convenient Bay Area Locations</h2>
        <p className="adults-section-sub">
          Find a class near you — all locations offer the same high-quality, supportive learning environment.
        </p>

        <div className="adults-locations-grid">
          {LOCATIONS.map(loc => (
            <div key={loc.city} className="adults-location-card">
              <span className="adults-location-icon">{loc.emoji}</span>
              <span className="adults-location-city">{loc.city}</span>
              <span className="adults-location-state">{loc.state}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ── COURSES ── */}
      <div id="curriculum" className="adults-section-alt">
        <div className="adults-section">
          <p className="adults-section-label">Our Curriculum</p>
          <h2 className="adults-section-title">Two Courses, One Journey</h2>
          <p className="adults-section-sub">
            Start with the fundamentals and build your confidence module by module, at your own pace.
          </p>

          <div className="adults-courses-grid">
            {/* Course 1 */}
            <div className="adults-course-card">
              <div className="adults-course-header">
                <div className="adults-course-icon-wrap adults-course-icon-teal">💻</div>
                <div className="adults-course-title">Computer Basics &amp; Modern Technology</div>
              </div>
              <p className="adults-course-desc">
                Learn essential computer skills, internet safety, email, and everyday digital tools.
                We also explain Artificial Intelligence (AI) in simple terms — technology that helps
                devices answer questions, organize information, and automate everyday tasks.
              </p>
              <div className="adults-topics-list">
                {COURSE_1_TOPICS.map(t => (
                  <div key={t} className="adults-topic-item">
                    <span className="adults-topic-check">✓</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
              <span className="adults-course-diff adults-diff-beginner">All Levels</span>
              <button className="adults-preview-btn" onClick={() => openModal('course1')}>
                🎬 Preview Course Content
              </button>
            </div>

            {/* Course 2 */}
            <div className="adults-course-card adults-course-card-sky">
              <div className="adults-course-header">
                <div className="adults-course-icon-wrap adults-course-icon-sky">💰</div>
                <div className="adults-course-title">FinTech Literacy &amp; Cognitive Wellness</div>
              </div>
              <p className="adults-course-desc">
                Understand modern financial tools such as digital banking, budgeting, fraud prevention,
                and online payments while engaging in activities that support memory, focus, and
                long-term cognitive well-being.
              </p>
              <div className="adults-topics-list">
                {COURSE_2_TOPICS.map(t => (
                  <div key={t} className="adults-topic-item">
                    <span className="adults-topic-check">✓</span>
                    <span>{t}</span>
                  </div>
                ))}
              </div>
              <span className="adults-course-diff adults-diff-intermediate">All Levels</span>
              <button className="adults-preview-btn" onClick={() => openModal('course2')}>
                🎬 Preview Course Content
              </button>
            </div>
          </div>

          <div className="adults-courses-enroll-cta">
            <a
              href="https://www.siliconvalley4u.com/lifelong-learning"
              target="_blank"
              rel="noopener noreferrer"
              className="adults-ngk-btn"
            >
              Enroll Now →
            </a>
          </div>
        </div>
      </div>

      {/* ── GAMIFICATION FOR ADULTS ── */}
      <div className="adults-section">
        <p className="adults-section-label">Track Your Journey</p>
        <h2 className="adults-section-title">Progress You Can See</h2>
        <p className="adults-section-sub">
          Learning feels more rewarding when you can see exactly how far you've come.
          Our platform makes your progress visible and motivating. Whether you're 55 or 75,
          building digital confidence is within reach.
        </p>

        <div className="adults-gamif-grid">
          <div className="adults-gamif-card">
            <div className="adults-gamif-icon">📈</div>
            <div className="adults-gamif-title">Self-Paced Learning</div>
            <p className="adults-gamif-desc">
              Learn on your schedule. Come back any time to pick up right where you left off —
              no pressure, no rush.
            </p>
          </div>
          <div className="adults-gamif-card">
            <div className="adults-gamif-icon">🎯</div>
            <div className="adults-gamif-title">Visual Progress Tracking</div>
            <p className="adults-gamif-desc">
              See your course completion grow module by module. Your confidence grows
              alongside your progress bar.
            </p>
          </div>
          <div className="adults-gamif-card">
            <div className="adults-gamif-icon">👥</div>
            <div className="adults-gamif-title">Supportive Community</div>
            <p className="adults-gamif-desc">
              Learn alongside peers who share your goals. Celebrate milestones together
              in a welcoming, judgment-free environment.
            </p>
          </div>
        </div>

        {/* Progress bar demo */}
        <div className="adults-progress-demo">
          <div className="adults-progress-header">
            <span className="adults-progress-label">Course Progress</span>
            <span className="adults-progress-count">2 of 5 modules complete</span>
          </div>
          <div className="adults-progress-bar">
            <div className="adults-progress-fill" />
          </div>
          <p className="adults-progress-caption">
            Your confidence grows module by module, at your own pace.
          </p>
        </div>

        <div className="adults-gamif-cta">
          <a
            href="https://www.nexgenklick.ai/landing"
            target="_blank"
            rel="noopener noreferrer"
            className="adults-ngk-btn"
          >
            Create Your NexGenKlick Profile →
          </a>
        </div>
      </div>

      {/* ── TESTIMONIAL ── */}
      <div className="adults-section-alt">
        <div className="adults-section">
          <p className="adults-section-label">What Participants Say</p>
          <h2 className="adults-section-title">Real Stories from Real Learners</h2>

          <div className="adults-testimonial">
            <p className="adults-testimonial-quote">
              "I never thought I'd understand digital banking — it always seemed so complicated
              and risky. This course made it simple, step by step, and completely stress-free.
              Now I handle all my banking online with total confidence."
            </p>
            <span className="adults-testimonial-author">— Margaret, 68 · San Mateo, CA</span>
          </div>
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <div className="adults-section">
        <div className="adults-cta-card">
          <h3>Ready to Build Your Digital Confidence?</h3>
          <p>
            Contact us to find a class near you or learn about our next session start dates.
            We'd love to welcome you to the community.
          </p>

          <div className="adults-cta-contact">
            <span className="adults-contact-item">📞 (408) 505-5499</span>
            <span className="adults-contact-item">✉ info@siliconvalley4u.com</span>
            <span className="adults-contact-item">🌐 www.siliconvalley4u.com</span>
          </div>

          <div className="adults-cta-actions">
            <a
              href="https://www.siliconvalley4u.com/lifelong-learning"
              target="_blank"
              rel="noopener noreferrer"
              className="adults-ngk-btn"
            >
              Enroll in a Course →
            </a>
            <a
              href="https://www.nexgenklick.ai/landing"
              target="_blank"
              rel="noopener noreferrer"
              className="adults-k12-link"
              style={{ textDecoration: 'none' }}
            >
              Build Portfolio on NexGenKlick →
            </a>
            <button className="adults-k12-link" onClick={onBack}>
              View All Programs
            </button>
          </div>
        </div>
      </div>

      {/* ── COURSE PREVIEW MODAL ── */}
      {coursePreview && (
        <div className="adults-modal-overlay" onClick={closeModal}>
          <div className="adults-modal" onClick={e => e.stopPropagation()}>
            <button className="adults-modal-close" onClick={closeModal}>✕</button>
            <div className="adults-modal-icon">
              {coursePreview === 'course1' ? '💻' : '💰'}
            </div>
            <h3 className="adults-modal-title">
              {coursePreview === 'course1'
                ? 'Computer Basics & Modern Technology'
                : 'FinTech Literacy & Cognitive Wellness'}
            </h3>

            {/* ── COURSE IMAGE (replaces video stub) ── */}
            {coursePreview === 'course1' ? <Course1Image /> : <Course2Image />}

            {/* ── MODULE LIST + QUIZ ── */}
            <div className="adults-modal-placeholder">
              {!moduleQuiz ? (
                <>
                  <p className="adults-modal-desc">
                    Explore each module below and try a sample quiz to see what you'll learn in class.
                  </p>
                  <div className="adults-modal-modules">
                    <p className="adults-modal-modules-label">Sample Modules</p>
                    {topics.map(t => (
                      <div key={t} className="adults-modal-module-item">
                        <span className="adults-modal-module-icon">📋</span>
                        <span>{t}</span>
                        <button
                          className="adults-modal-quiz-btn"
                          onClick={() => startQuiz(t)}
                        >
                          🧠 Sample Quiz
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              ) : moduleQuiz.done ? (
                /* ── QUIZ RESULTS ── */
                (() => {
                  const questions = quizData[moduleQuiz.topic];
                  const score = moduleQuiz.answers.filter(
                    (ans, i) => ans === questions[i].answer
                  ).length;
                  const pct = Math.round((score / questions.length) * 100);
                  return (
                    <div className="adults-quiz-results">
                      <div className="adults-quiz-score-emoji">
                        {pct === 100 ? '🎉' : pct >= 70 ? '👍' : '💪'}
                      </div>
                      <p className="adults-quiz-topic-label">{moduleQuiz.topic}</p>
                      <p className="adults-quiz-score-text">
                        {score} / {questions.length} correct
                      </p>
                      <p className="adults-quiz-score-msg">
                        {coursePreview === 'course1'
                          ? pct === 100
                            ? 'Impressive! Enroll now to go even deeper and earn your certificate!'
                            : pct >= 70
                            ? 'Great work! Join the class to sharpen your digital skills even further.'
                            : 'This course will walk you through every step — you\'ll be a confident tech user in no time!'
                          : pct === 100
                            ? 'Outstanding! Enroll now to put these skills to work and protect your financial future!'
                            : pct >= 70
                            ? 'Nice work! The course will help you apply these skills to your real finances with confidence.'
                            : 'This course will guide you every step of the way — your financial confidence starts here!'}
                      </p>
                      <button
                        className="adults-quiz-restart-btn"
                        onClick={() => setModuleQuiz(null)}
                      >
                        ← Back to Modules
                      </button>
                    </div>
                  );
                })()
              ) : (
                /* ── ACTIVE QUIZ QUESTION ── */
                (() => {
                  const questions = quizData[moduleQuiz.topic];
                  const q = questions[moduleQuiz.qIdx];
                  const revealed = moduleQuiz.selected !== null;
                  const isRight = revealed && moduleQuiz.selected === q.answer;
                  return (
                    <div className="adults-quiz-active">
                      <div className="adults-quiz-header">
                        <span className="adults-quiz-topic-badge">{moduleQuiz.topic}</span>
                        <span className="adults-quiz-progress">
                          {moduleQuiz.qIdx + 1} / {questions.length}
                        </span>
                      </div>
                      <p className="adults-quiz-question">{q.question}</p>
                      <div className="adults-quiz-options">
                        {q.options.map((opt, i) => {
                          const isCorrect = i === q.answer;
                          let cls = 'adults-quiz-option';
                          if (revealed && isCorrect) cls += ' correct';
                          else if (revealed && moduleQuiz.selected === i) cls += ' wrong';
                          return (
                            <button
                              key={i}
                              className={cls}
                              onClick={() =>
                                moduleQuiz.selected === null &&
                                setModuleQuiz({ ...moduleQuiz, selected: i })
                              }
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                      {revealed && isRight && (
                        <div className="adults-quiz-feedback adults-quiz-feedback--correct">
                          ✓ Correct!
                        </div>
                      )}
                      {revealed && !isRight && (
                        <div className="adults-quiz-feedback adults-quiz-feedback--wrong">
                          The correct answer is highlighted in green.
                        </div>
                      )}
                      <div className="adults-quiz-footer">
                        <button
                          className="adults-quiz-back-btn"
                          onClick={() => setModuleQuiz(null)}
                        >
                          ← Back
                        </button>
                        {moduleQuiz.selected !== null && (
                          <button
                            className="adults-quiz-next-btn"
                            onClick={() => {
                              const newAnswers = [...moduleQuiz.answers, moduleQuiz.selected];
                              if (moduleQuiz.qIdx + 1 < questions.length) {
                                setModuleQuiz({
                                  ...moduleQuiz,
                                  qIdx: moduleQuiz.qIdx + 1,
                                  selected: null,
                                  answers: newAnswers,
                                });
                              } else {
                                setModuleQuiz({ ...moduleQuiz, done: true, answers: newAnswers });
                              }
                            }}
                          >
                            {moduleQuiz.qIdx + 1 < questions.length ? 'Next →' : 'See Results'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()
              )}
            </div>

            <a
              href="https://www.siliconvalley4u.com/lifelong-learning"
              target="_blank"
              rel="noopener noreferrer"
              className="adults-ngk-btn"
            >
              Enroll in This Course →
            </a>
          </div>
        </div>
      )}

    </div>
  );
}

export default AdultsPreview;
